import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation
} from "react-router-dom";

import Home from "./components/home";
import About from "./components/about";
import Dashboard from "./components/dashboard";
import Page1 from "./components/page1";
import Logout from "./components/logout";
import ProtectedRoute from "./components/protected";

import GlobalMap from "./components/workerMap";
import PatientList from "./components/patientList";
import WorkerList from "./components/workerList";
import CreateGroup from "./components/CreateGroup";
import AdminHome from "./components/AdminHome";
import AdminUploadDocuments from "./pages/AdminUploadDocuments";
import AdminQueryDocuments from "./pages/AdminQueryDocuments";
import AdminPlaceholderPage from "./pages/AdminPlaceholderPage";
import AdminAnalyticsPage from "./pages/AdminAnalyticsPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import AdminProfileSetup from "./pages/AdminProfileSetup";
import AdminCreateGroupOnboarding from "./pages/AdminCreateGroupOnboarding";
import AdminProfilePage from "./pages/AdminProfilePage";
import AcceptInvite from "./components/AcceptInvite";
import GroupDetails from "./components/GroupDetails";
import DashboardRouter from "./components/DashboardRouter";
import Remotelogin from "./components/remotelogin";
import Remoteregister from "./components/remoteregister";
import RemoteProfileEdit from "./components/remoteProfileEdit";
import RemoteProfileView from "./components/remoteProfileViewer";
import Patientlogin from "./components/patientlogin";
import PatientProfileEdit from "./components/patientProfileEdit";
import Patientregister from "./components/patientregister";
import UserHome from "./components/UserHome";
import PatientMap from "./components/patientmap";
import WorkerHome from "./components/WorkerHome";
import ActivatedPatients from "./components/activatedpatients";
import WorkerElderProfilePage from "./components/WorkerElderProfilePage";
import PatientProfileView from "./components/patientProfileViewer";
import AgentsPage from "./components/AgentsPage";
import WorkerAgentsPage from "./components/WorkerAgentsPage";
import WorkerAgentWorkspace from "./components/WorkerAgentWorkspace";
import logo from "./images/cape.png";
import config from "./config";
import PatientLayout from "./components/patientlayout";
import WorkerLayout from "./components/workerlayout";
import ActivatedPatientMap from "./components/activatedPatientMap";

if (typeof document !== "undefined" && !document.getElementById("vault-pulse-keyframes")) {
  const style = document.createElement("style");
  style.id = "vault-pulse-keyframes";
  style.textContent = `
    @keyframes vaultPulse {
      0% { transform: scale(1); opacity: 0.96; }
      50% { transform: scale(1.08); opacity: 1; }
      100% { transform: scale(1); opacity: 0.96; }
    }
  `;
  document.head.appendChild(style);
}


/* =====================
   NAVBAR + ROUTE LOGIC
===================== */
const AppLayout = () => {
  const location = useLocation();
  const apiBase = config.API_BASE_URL || "http://localhost:5003";
  const token = localStorage.getItem("token");
  const isHomeRoute = location.pathname === "/";
  const isAdminWorkspaceRoute =
    location.pathname === "/admin-home" ||
    location.pathname === "/admin/home" ||
    location.pathname === "/admin/create-group" ||
    location.pathname === "/admin/profile-setup" ||
    location.pathname === "/create-group" ||
    location.pathname === "/admin-upload-documents" ||
    location.pathname === "/admin-query-documents" ||
    location.pathname === "/admin/documents/upload" ||
    location.pathname === "/admin/documents/query" ||
    location.pathname === "/admin/agentic-services" ||
    location.pathname === "/admin/analytics" ||
    location.pathname === "/admin/profile" ||
    location.pathname === "/admin/profile/edit" ||
    location.pathname.startsWith("/admin/groups/");

  // ❌ Pages that should NOT show the top navbar
  const hideNavbarRoutes = [
     "/user-home",
    "/worker-home",
    "/patientmap",
     "/remoteProfileEdit",
     "/remoteProfileViewer",
     "/activatedpatients",  
     "/worker-agents",
     "/worker-agent-chat",
     "/worker-elder-profile",
     "/patientProfileEdit",
     "/patientProfileViewer",
     "/activatedPatientMap",
     "/agents",
  ];

  const hideNavbar =
    hideNavbarRoutes.includes(location.pathname) ||
    location.pathname.startsWith("/worker/patients/");
  const [groupName, setGroupName] = useState("");
  const [vaultActive, setVaultActive] = useState(false);

  useEffect(() => {
    if (!isAdminWorkspaceRoute || !token) return;
    let mounted = true;

    fetch(`${apiBase}/admin/group`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then((res) => (res.ok ? res.json() : { groupName: "" }))
      .then((data) => {
        if (!mounted) return;
        const name = String(data?.groupName || "").trim();
        setGroupName(name);
        if (name) localStorage.setItem("groupName", name);
      })
      .catch(() => {
        if (!mounted) return;
        setGroupName(localStorage.getItem("groupName") || "");
      });

    return () => {
      mounted = false;
    };
  }, [apiBase, isAdminWorkspaceRoute, token, location.pathname]);

  useEffect(() => {
    if (!isAdminWorkspaceRoute) return;

    let mounted = true;

    const syncVaultStatus = async () => {
      try {
        const response = await fetch(`${apiBase}/system/status`);
        const payload = response.ok ? await response.json() : {};
        if (!mounted) return;
        setVaultActive(Boolean(payload?.ollama && payload?.modelAvailable));
      } catch {
        if (!mounted) return;
        setVaultActive(false);
      }
    };

    syncVaultStatus();
    const interval = setInterval(syncVaultStatus, 15000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [apiBase, isAdminWorkspaceRoute]);

  return (
    <>
      {/* =====================
          TOP NAVBAR
      ===================== */}
      {!hideNavbar && (
        <header style={navBar}>
          {isHomeRoute ? (
            <>
              <div style={homeBannerLeft}>
                <Link to="/">
                  <img src={logo} alt="Kin-Keepers Logo" style={logoStyle} />
                </Link>
                <span style={welcomeCopy}>Welcome to Family Circle</span>
              </div>

              <div style={homeBannerCenter} />

              <div style={homeBannerRight}>
                <Link style={loginCta} to="/admin/login">Kin-Keeper Login</Link>
                <Link style={registerCta} to="/admin/register">Kin-Keeper Register</Link>
              </div>
            </>
          ) : (
            <>
              <div style={navLeft}>
                <Link to="/">
                  <img src={logo} alt="Logo" style={logoStyle} />
                </Link>

                <Link to="/" style={brand}>
                  {isAdminWorkspaceRoute ? (
                    <>
                      <span style={brandLabel}>Family Circle</span>
                      {groupName ? <span style={groupNameText}>: {groupName}</span> : null}
                    </>
                  ) : (
                    "Family Circle"
                  )}
                </Link>
              </div>

              <nav style={navRight}>
                {isAdminWorkspaceRoute ? (
                  <div style={modeRow}>
                    <span style={modeTitle}>Mode</span>
                    <div
                      style={modeStatusCol}
                      title={
                        vaultActive
                          ? "AI system ready for queries"
                          : "AI system is currently not running"
                      }
                    >
                      <p style={modeItem}>
                        <span
                          style={{
                            ...dot,
                            backgroundColor: vaultActive ? "#22c55e" : "#94a3b8",
                            boxShadow: vaultActive
                              ? "0 0 0 4px rgba(34, 197, 94, 0.16), 0 0 14px rgba(34, 197, 94, 0.42)"
                              : "0 0 0 4px rgba(148, 163, 184, 0.14)",
                            animation: vaultActive ? "vaultPulse 1.5s infinite" : "none"
                          }}
                        />
                        {vaultActive ? "Vault Active" : "Vault Not Active"}
                      </p>
                    </div>
                  </div>
                ) : null}
                <Link style={navLink} to={isAdminWorkspaceRoute ? "/admin/home" : "/"}>Home</Link>
                <div style={divider} />
                <div style={logoutWrapper}>
                  <Logout />
                </div>
              </nav>
            </>
          )}
        </header>
      )}

      {/* =====================
          ROUTES
      ===================== */}
      <main style={{ marginTop: hideNavbar ? "0" : "112px" }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<About />} />
          <Route path="/admin/login" element={<About />} />
          <Route path="/register" element={<Page1 />} />
          <Route path="/admin/register" element={<Page1 />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />

          <Route path="/worker-home" element={<ProtectedRoute role="worker"><WorkerLayout /><WorkerHome /></ProtectedRoute>} />
          <Route path="/user-home" element={<ProtectedRoute><PatientLayout /><UserHome /></ProtectedRoute>} />
          <Route path="/admin-home" element={<ProtectedRoute><AdminHome /></ProtectedRoute>} />
          <Route path="/admin/home" element={<ProtectedRoute><AdminHome /></ProtectedRoute>} />
          <Route path="/admin/profile-setup" element={<ProtectedRoute><AdminProfileSetup /></ProtectedRoute>} />
          <Route path="/admin/create-group" element={<ProtectedRoute><AdminCreateGroupOnboarding /></ProtectedRoute>} />
          <Route path="/admin-upload-documents" element={<ProtectedRoute><AdminUploadDocuments /></ProtectedRoute>} />
          <Route path="/admin-query-documents" element={<ProtectedRoute><AdminQueryDocuments /></ProtectedRoute>} />
          <Route path="/admin/documents/upload" element={<ProtectedRoute><AdminUploadDocuments /></ProtectedRoute>} />
          <Route path="/admin/documents/query" element={<ProtectedRoute><AdminQueryDocuments /></ProtectedRoute>} />
          <Route path="/create-group" element={<ProtectedRoute><CreateGroup /></ProtectedRoute>} />
          <Route
            path="/admin/agentic-services"
            element={<ProtectedRoute><AdminPlaceholderPage activeItem="agentic" title="Agentic Services" subtitle="Biometrics, UberCare, and marketplace integrations are under development." /></ProtectedRoute>}
          />
          <Route
            path="/admin/analytics"
            element={<ProtectedRoute><AdminAnalyticsPage /></ProtectedRoute>}
          />
          <Route
            path="/admin/profile"
            element={<ProtectedRoute><AdminSettingsPage /></ProtectedRoute>}
          />
          <Route
            path="/admin/profile/edit"
            element={<ProtectedRoute><AdminProfilePage /></ProtectedRoute>}
          />
          <Route path="/admin/groups/:groupId" element={<ProtectedRoute><GroupDetails /></ProtectedRoute>} />
          <Route path="/dashboard-router" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/workerMap" element={<ProtectedRoute><GlobalMap /></ProtectedRoute>} />
          <Route path="/patientList" element={<ProtectedRoute><PatientList /></ProtectedRoute>} />
          <Route path="/workerList" element={<ProtectedRoute><WorkerList /></ProtectedRoute>} />

          <Route path="/remotelogin" element={<Remotelogin />} />
          <Route path="/remoteregister" element={<Remoteregister />} />
          <Route path="/remoteProfileEdit" element={<ProtectedRoute role="worker"><WorkerLayout /><RemoteProfileEdit/></ProtectedRoute>} />

          <Route path="/patientlogin" element={<Patientlogin />} />
          <Route path="/patientregister" element={<Patientregister />} />
          <Route path="/patientProfileEdit" element={<ProtectedRoute><PatientLayout /><PatientProfileEdit /></ProtectedRoute>} />
          <Route path="/patientmap" element={<ProtectedRoute><PatientMap /></ProtectedRoute>} />
          <Route path="/agents" element={<ProtectedRoute><AgentsPage /></ProtectedRoute>} />
          <Route path="/activatedpatients" element={<ProtectedRoute role="worker"><WorkerLayout /><ActivatedPatients /></ProtectedRoute>} />
          <Route path="/worker/patients/:patientId" element={<ProtectedRoute role="worker"><WorkerLayout /><WorkerElderProfilePage /></ProtectedRoute>} />
          <Route path="/worker-agents" element={<ProtectedRoute role="worker"><WorkerLayout /><WorkerAgentsPage /></ProtectedRoute>} />
          <Route path="/worker-agent-chat" element={<ProtectedRoute role="worker"><WorkerLayout /><WorkerAgentWorkspace /></ProtectedRoute>} />
           <Route path="/activatedPatientMap" element={<ProtectedRoute role="worker"><ActivatedPatientMap /></ProtectedRoute>} />
           <Route path="/remoteProfileViewer" element={<ProtectedRoute role="worker"><WorkerLayout /><RemoteProfileView/></ProtectedRoute>} />
           <Route path="/patientProfileViewer" element={<ProtectedRoute><PatientLayout /><PatientProfileView /></ProtectedRoute>} />
        </Routes>
      </main>
    </>
  );
};

/* =====================
   ROOT APP
===================== */
const App = () => {
  return (
    <Router>
      <AppLayout />
    </Router>
  );
};

export default App;

/* =====================
   STYLES (UNCHANGED)
===================== */

const navBar = {
  height: "112px",
  background: "#0E5580",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 36px",
  position: "fixed",
  top: 0,
  width: "100%",
  boxSizing: "border-box",
  zIndex: 1000
};

const homeBannerLeft = {
  display: "flex",
  alignItems: "center",
  gap: "18px",
  minWidth: 0
};

const homeBannerCenter = {
  flex: 1
};

const homeBannerRight = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap",
  justifyContent: "flex-end"
};

const navLeft = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  minWidth: 0,
  flex: 1
};

const logoStyle = {
  height: "66px"
};

const brand = {
  fontSize: "22px",
  fontWeight: "600",
  letterSpacing: "0.2px",
  color: "#FFD700",
  textDecoration: "none",
  lineHeight: 1.2,
  maxWidth: "58vw",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "baseline"
};

const brandLabel = {
  fontSize: "28px",
  fontWeight: "600"
};

const groupNameText = {
  fontSize: "28px",
  fontWeight: "700",
  color: "#ffffff"
};

const navRight = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap",
  justifyContent: "flex-end"
};

const modeRow = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginRight: "8px"
};

const modeTitle = {
  margin: 0,
  color: "#FFD700",
  fontSize: "25px",
  fontWeight: "600",
  lineHeight: 1.2,
  letterSpacing: "0.2px"
};

const modeStatusCol = {
  display: "grid",
  gap: "2px"
};

const modeItem = {
  margin: 0,
  color: "#fff",
  fontSize: "18px",
  fontWeight: "500",
  display: "flex",
  alignItems: "center",
  gap: "6px"
};

const dot = {
  width: "16px",
  height: "16px",
  borderRadius: "50%",
  display: "inline-block"
};

const navLink = {
  color: "#fff",
  fontSize: "clamp(18px, 1.7vw, 24px)",
  fontWeight: "700",
  textDecoration: "none",
  cursor: "pointer"
};

const divider = {
  width: "1px",
  height: "28px",
  background: "rgba(255,255,255,0.4)"
};

const logoutWrapper = {
  paddingLeft: "15px",
  display: "flex",
  alignItems: "center"
};

const welcomeCopy = {
  color: "#FFD700",
  fontSize: "clamp(24px, 2.6vw, 34px)",
  fontWeight: "700",
  letterSpacing: "0.5px",
  whiteSpace: "nowrap"
};

const loginCta = {
  textDecoration: "none",
  background: "transparent",
  border: "1px solid #d4e7f3",
  color: "#fff",
  borderRadius: "10px",
  fontWeight: "700",
  padding: "10px 14px",
  fontSize: "14px"
};

const registerCta = {
  textDecoration: "none",
  background: "#FFD700",
  border: "1px solid #FFD700",
  color: "#0E5580",
  borderRadius: "10px",
  fontWeight: "800",
  padding: "10px 14px",
  fontSize: "14px"
};
